/**
 * F-HS-27's unified read model, as a pure derivation.
 *
 * Training history lives in two stores. The canonical one holds everything this
 * build writes; the legacy one holds records adapted from the shipped iOS app.
 * The merge rule is one sentence — **native sessions always win uuid
 * collisions** — and the reason it matters is that a legacy record is read-only
 * and is never dual-written, so preferring it would show the user a stale copy
 * of a session they have since edited.
 *
 * The port declares the merge is "a pure derivation over both reads" and
 * deliberately does not perform it, but no package exports it either. It lives
 * here so this packet has one, and it belongs in `@ff/domain` beside
 * `sortHistoryNewestFirst` — History (P40) needs the identical derivation and a
 * second copy of it is a second place the collision rule can be got wrong. See
 * the packet's contract report.
 */

import { sortHistoryNewestFirst } from "@ff/domain";
import type { EffortSessionFields } from "@ff/domain";

export function mergeEffortHistory(
  canonical: readonly EffortSessionFields[],
  legacy: readonly EffortSessionFields[],
): EffortSessionFields[] {
  const byId = new Map<string, EffortSessionFields>();
  // Canonical first, then legacy with `has` guarding every insert: a legacy
  // record contributes only an id the canonical store does not already claim.
  for (const session of canonical) byId.set(session.id, session);
  for (const session of legacy) {
    if (!byId.has(session.id)) byId.set(session.id, session);
  }
  return sortHistoryNewestFirst([...byId.values()]);
}
